import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl-next';
import { LoadingState, MarkRequestReadState, ReadState as RenderableReadState } from '@atlassian/notification-types/types';
import { triggerDocumentCollapsed, triggerDocumentExpanded, triggerKeyboardShortcutUsed, triggerMarkOneReadButtonClicked, triggerNotificationLinkClicked, triggerRequestNotificationViewed, useCreateFireAnalyticsFromTrigger } from '../../utils/analytics';
import { LinkType } from '../../utils/analytics/types';
import { ExperienceMark, ExperienceSuccess, NotificationExperienceMarkKeys } from '../../utils/experience-tracking/adapter';
import { Experiences } from '../../utils/experience-tracking/types';
import { getUrlWithAtlOrigin } from '../../utils/getUrlWithAtlOrigin';
import { isDesNotification } from '../../utils/is-des-notification';
import { isRequestNotification } from '../../utils/is-request-notification';
import { useFocus } from '../../utils/useFocus';
import { keyboardEventToAction, useFocusManager } from '../../utils/useFocusManager';
import { KeyboardActionToAnalyticSubject } from '../../utils/useFocusManager/types';
import { AvatarSkeleton } from '../avatar-layout';
import { AvatarThread, PositionInGroup } from '../avatar-thread';
import ErrorBoundary from '../error-boundary';
import FormattedText, { useFormattedTextValues } from '../formatted-text';
import { useOriginId } from '../notifications-context/main';
import AdditionalCtaButtons from './additional-cta-buttons';
import AtlassianAvatarContainer from './atlassian-avatar-container';
import AvatarContainer from './avatar-container';
import Document from './document';
import Entity from './entity';
import MessageWithTimestamp from './message-with-timestamp';
import Path from './path';
import ReadStateDropdownMenu from './read-state-dropdown-menu';
import ReadStateIndicator from './read-state-indicator';
import { BackgroundFadeOut, EnableMouseEventsDocumentWrapper, EnableMouseEventsProfileWrapper, FlexRow, FullWidth, InboxReadStateIndicatorContainer, ItemContainer, MainAction, NotificationContainer, SummaryContainer } from './styled';
import { buildPathText, getMainActionUrl, getPathUrl, selectUnit } from './utils';
import ViewedTracker from './viewed-tracker';
export { BackgroundFadeOut } from './styled';
function NotificationItem(_ref) {
  var _notification$content, _notification$content2, _notification$content3, _notification$content4, _notification$content5, _notification$content6, _notification$content7, _notification$content8;
  var notification = _ref.notification,
    forceContentLoading = _ref.forceContentLoading,
    listIndex = _ref.listIndex,
    markBulkNotifications = _ref.markBulkNotifications,
    className = _ref.className,
    positionInGroup = _ref.positionInGroup,
    _ref$expandGroupButto = _ref.expandGroupButton,
    expandGroupButton = _ref$expandGroupButto === void 0 ? null : _ref$expandGroupButto,
    displayReadStateContextMenu = _ref.displayReadStateContextMenu,
    _ref$onGroupReadState = _ref.onGroupReadStateChange,
    onGroupReadStateChange = _ref$onGroupReadState === void 0 ? function () {} : _ref$onGroupReadState,
    _ref$groupReadStates = _ref.groupReadStates,
    groupReadStates = _ref$groupReadStates === void 0 ? [] : _ref$groupReadStates,
    onClick = _ref.onClick,
    _ref$readStateIndicat = _ref.readStateIndicatorStyle,
    readStateIndicatorStyle = _ref$readStateIndicat === void 0 ? 'standard' : _ref$readStateIndicat;
  var intl = useIntl();
  var originId = useOriginId();
  var isRequestNotificationType = isRequestNotification(notification);
  var isDesNotificationType = isDesNotification(notification);
  var showAdditionalCtaButtons = isRequestNotificationType || ((_notification$content = notification.content) === null || _notification$content === void 0 ? void 0 : _notification$content.actions) && ((_notification$content2 = notification.content) === null || _notification$content2 === void 0 ? void 0 : _notification$content2.actions.length) >= 2 && ((_notification$content3 = notification.content) === null || _notification$content3 === void 0 ? void 0 : _notification$content3.actions.length) <= 3;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isExpanded = _useState2[0],
    setIsExpanded = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    hasUserInteracted = _useState4[0],
    setHasUserInteracted = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    markAsReadActive = _useState6[0],
    setMarkAsReadActive = _useState6[1];
  var fireRequestNotificationViewed = useCreateFireAnalyticsFromTrigger(triggerRequestNotificationViewed);
  var fireTriggerKeyboardShortcutUsed = useCreateFireAnalyticsFromTrigger(triggerKeyboardShortcutUsed);
  var fireNotificationLinkClicked = useCreateFireAnalyticsFromTrigger(triggerNotificationLinkClicked);
  var fireMarkOneReadClicked = useCreateFireAnalyticsFromTrigger(triggerMarkOneReadButtonClicked);
  var fireTriggerDocumentExpanded = useCreateFireAnalyticsFromTrigger(triggerDocumentExpanded);
  var fireTriggerDocumentCollapsed = useCreateFireAnalyticsFromTrigger(triggerDocumentCollapsed);
  useEffect(function () {
    if (isRequestNotificationType) {
      fireRequestNotificationViewed();
    }
  }, [isRequestNotificationType, fireRequestNotificationViewed]);
  var ref = useRef(null);
  var _useFocusManager = useFocusManager(ref, listIndex),
    focused = _useFocusManager.focused,
    handleKeydown = _useFocusManager.handleKeydown,
    handleOnFocus = _useFocusManager.handleOnFocus,
    handleOnBlur = _useFocusManager.handleOnBlur;
  useFocus(ref, focused);
  var pathText = buildPathText(notification.content.path, notification.content.entity);
  var pathUrl = getPathUrl(notification.content.entity, notification.content.path);
  var handleToggleReadState = function handleToggleReadState() {
    var toState = notification.readState === RenderableReadState.READ ? MarkRequestReadState.UNREAD : MarkRequestReadState.READ;
    fireMarkOneReadClicked(toState, notification, listIndex);
    markBulkNotifications([notification], toState);
  };
  var toggleExpanded = function toggleExpanded() {
    var newIsExpanded = !isExpanded;
    setIsExpanded(newIsExpanded);
    if (newIsExpanded) {
      fireTriggerDocumentExpanded(notification, listIndex);
    } else {
      fireTriggerDocumentCollapsed(notification, listIndex);
    }
  };
  var handleClick = useCallback(function (linkType, clickType, event) {
    var result = onClick === null || onClick === void 0 ? void 0 : onClick(linkType, clickType, event);
    var isUnread = notification.readState === RenderableReadState.UNREAD;
    var shouldMarkAsRead = function shouldMarkAsRead() {
      if ((result === null || result === void 0 ? void 0 : result.shouldMarkAsRead) === false) {
        return false;
      }
      return clickType === 'left' || (result === null || result === void 0 ? void 0 : result.shouldMarkAsRead);
    };
    if (isUnread && shouldMarkAsRead()) {
      markBulkNotifications([notification], MarkRequestReadState.READ);
    }
    fireNotificationLinkClicked(notification, listIndex, linkType, clickType);
  }, [fireNotificationLinkClicked, listIndex, markBulkNotifications, notification, onClick]);
  var getMainActionLabel = function getMainActionLabel() {
    if (!notification.content.actions || notification.content.actions.length === 0) {
      return notification.content.message;
    }
    return notification.content.actions[0].title;
  };
  var mainActionUrl = getUrlWithAtlOrigin(getMainActionUrl(notification.content.actions, notification.content.entity), originId);
  var expectedBodyItemCount = notification.bodyItemCount;
  var actualBodyItemCount = (_notification$content4 = (_notification$content5 = notification.content) === null || _notification$content5 === void 0 || (_notification$content5 = _notification$content5.body) === null || _notification$content5 === void 0 ? void 0 : _notification$content5.items.length) !== null && _notification$content4 !== void 0 ? _notification$content4 : 0;
  var bodyItemCount = notification.loadingState === LoadingState.COMPLETE ? actualBodyItemCount : expectedBodyItemCount;
  var onKeyDownHandler = function onKeyDownHandler(event) {
    setHasUserInteracted(true);
    switch (event.key) {
      case 'r':
        {
          fireTriggerKeyboardShortcutUsed('markNotificationRead', notification, listIndex);
          handleToggleReadState();
          return;
        }
      case 'e':
        {
          fireTriggerKeyboardShortcutUsed('expandNotification', notification, listIndex);
          toggleExpanded();
          return;
        }
      default:
        {
          var shortCutActionType = keyboardEventToAction(event);
          if (shortCutActionType) {
            event.preventDefault(); // stop scroll on keydown events
            fireTriggerKeyboardShortcutUsed(KeyboardActionToAnalyticSubject(shortCutActionType), notification, listIndex);
          }
          handleKeydown(event);
        }
    }
  };
  var timestamp = selectUnit(new Date(notification.timestamp), Date.now());
  var timestampText = intl.formatRelativeTime(timestamp.value, timestamp.unit);
  var handleUserInteraction = function handleUserInteraction() {
    setHasUserInteracted(true);
  };
  var formattedTextValues = useFormattedTextValues(notification.content.templateVariables, notification.cloudId);
  var handleMainActionClick = function handleMainActionClick(clickType) {
    return function (event) {
      if (mainActionUrl) {
        handleClick(LinkType.MAIN_ACTION, clickType, event);
      }
    };
  };
  var renderReadStateIndicatorDropdownMenu = function renderReadStateIndicatorDropdownMenu() {
    return /*#__PURE__*/React.createElement(ReadStateDropdownMenu, {
      triggerComponent: ReadStateIndicator,
      triggerComponentProps: {
        handleActiveChange: setMarkAsReadActive,
        read: notification.readState === RenderableReadState.READ,
        testId: 'read-state-indicator',
        onToggle: handleToggleReadState,
        groupReadStates: positionInGroup ? undefined : groupReadStates
      },
      onToggle: handleToggleReadState,
      onGroupToggle: onGroupReadStateChange,
      groupReadStates: groupReadStates
    });
  };
  var renderReadStateIndicatorButton = function renderReadStateIndicatorButton() {
    return /*#__PURE__*/React.createElement(ReadStateIndicator, {
      handleActiveChange: setMarkAsReadActive,
      read: notification.readState === RenderableReadState.READ,
      onToggle: handleToggleReadState,
      testId: "read-state-indicator"
    });
  };
  var renderInboxStyleReadStateIndicator = function renderInboxStyleReadStateIndicator() {
    return displayReadStateContextMenu ? renderReadStateIndicatorDropdownMenu() : /*#__PURE__*/React.createElement(InboxReadStateIndicatorContainer, null, renderReadStateIndicatorButton());
  };
  var renderStandardStyleReadStateIndicator = function renderStandardStyleReadStateIndicator() {
    return displayReadStateContextMenu ? renderReadStateIndicatorDropdownMenu() : renderReadStateIndicatorButton();
  };
  return /*#__PURE__*/React.createElement(BackgroundFadeOut, {
    shouldFadeOut: notification.isUnseen
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
    ,
    className: className
  }, /*#__PURE__*/React.createElement(ViewedTracker, {
    notification: notification,
    index: listIndex,
    hasUserInteracted: hasUserInteracted
  }), /*#__PURE__*/React.createElement(ItemContainer, {
    "data-testid": "notification-item-container",
    "aria-describedby": notification.id + '_summary',
    "aria-posinset": listIndex,
    "aria-setsize": -1 // -1 As the total list size in indeterminate
    ,
    onMouseDown: handleUserInteraction,
    onTouchStart: handleUserInteraction,
    backgroundOnActive:
    // Prevent a notification focussing on click if there is no action
    (notification.readState === RenderableReadState.UNREAD || ((_notification$content6 = notification.content.entity) === null || _notification$content6 === void 0 ? void 0 : _notification$content6.link) !== undefined) && !markAsReadActive
  }, /*#__PURE__*/React.createElement(ExperienceSuccess, {
    name: Experiences.RENDER_NOTIFICATIONS_WITHOUT_CONTENT,
    attributes: {
      noNotificationsScreen: false
    }
  }), /*#__PURE__*/React.createElement(ExperienceMark, {
    mark: NotificationExperienceMarkKeys.NOTIFICATION_LIGHTWEIGHT_RENDER,
    phase: "end"
  }), /*#__PURE__*/React.createElement(MainAction, {
    ref: ref,
    onKeyDown: onKeyDownHandler,
    onFocus: handleOnFocus,
    onBlur: handleOnBlur,
    href: mainActionUrl,
    onClick: handleMainActionClick('left'),
    onAuxClick: handleMainActionClick('middle'),
    onContextMenu: handleMainActionClick('right'),
    "data-testid": "notification-item-main-action",
    "aria-label": getMainActionLabel() + ' ' + timestampText
  }), /*#__PURE__*/React.createElement(NotificationContainer, null, readStateIndicatorStyle === 'inbox' && renderInboxStyleReadStateIndicator(), /*#__PURE__*/React.createElement(EnableMouseEventsProfileWrapper, null, /*#__PURE__*/React.createElement(ErrorBoundary, {
    fallbackUI: /*#__PURE__*/React.createElement(AvatarSkeleton, null),
    subjectId: "avatarContainer"
  }, !isRequestNotificationType && !isDesNotificationType ? positionInGroup ? /*#__PURE__*/React.createElement(AvatarThread, {
    positionInGroup: positionInGroup
  }, /*#__PURE__*/React.createElement(AvatarContainer, {
    actors: notification.content.actors
  })) : /*#__PURE__*/React.createElement(AvatarContainer, {
    actors: notification.content.actors
  }) : /*#__PURE__*/React.createElement(AtlassianAvatarContainer, null))), /*#__PURE__*/React.createElement(FullWidth, null, /*#__PURE__*/React.createElement(FlexRow, null, /*#__PURE__*/React.createElement(SummaryContainer, null, /*#__PURE__*/React.createElement(MessageWithTimestamp, {
    id: notification.id + '_summary',
    message: /*#__PURE__*/React.createElement(FormattedText, {
      text: notification.content.message,
      values: formattedTextValues
    }),
    timestamp: timestampText
  }), (notification === null || notification === void 0 || (_notification$content7 = notification.content) === null || _notification$content7 === void 0 ? void 0 : _notification$content7.entity) && (positionInGroup === undefined || positionInGroup === PositionInGroup.FIRST) && /*#__PURE__*/React.createElement(Entity, {
    text: /*#__PURE__*/React.createElement(FormattedText, {
      text: notification.content.entity.title,
      values: formattedTextValues
    }),
    entity: notification.content.entity,
    onClick: handleClick
  }), ((_notification$content8 = notification.content) === null || _notification$content8 === void 0 ? void 0 : _notification$content8.path.length) > 0 && (positionInGroup === undefined || positionInGroup === PositionInGroup.FIRST) && /*#__PURE__*/React.createElement(Path, {
    url: getUrlWithAtlOrigin(pathUrl, originId),
    text: /*#__PURE__*/React.createElement(FormattedText, {
      text: pathText,
      values: formattedTextValues
    }),
    onClick: handleClick
  })), readStateIndicatorStyle === 'standard' && renderStandardStyleReadStateIndicator()), bodyItemCount > 0 && /*#__PURE__*/React.createElement(EnableMouseEventsDocumentWrapper, null, /*#__PURE__*/React.createElement(Document, {
    onToggleExpand: toggleExpanded,
    loadingState: forceContentLoading ? LoadingState.LOADING : notification.loadingState,
    notification: notification,
    notificationPosition: listIndex,
    isExpanded: isExpanded,
    cloudId: notification.cloudId,
    formattedTextValues: formattedTextValues
  })), !showAdditionalCtaButtons && expandGroupButton, showAdditionalCtaButtons && /*#__PURE__*/React.createElement(AdditionalCtaButtons, {
    notification: notification,
    markBulkNotifications: markBulkNotifications
  })))));
}
export default NotificationItem;